import { supabase } from "../supabase/supabase";
import {
  CampaignTemplate,
  CampaignTemplateInsert,
  CampaignTemplateUpdate,
  CampaignTemplateStatus,
  CampaignTemplateLead,
  CampaignTemplateLeadInsert,
} from "../types/campaign";

export const campaignTemplateService = {
  /**
   * Create a new campaign
   */
  async createCampaignTemplate(
    campaignTemplate: CampaignTemplateInsert,
  ): Promise<{ data: CampaignTemplate | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaign_templates")
        .insert(campaignTemplate)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error creating campaign template:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get all campaigns for an organization
   */
  async getCampaignTemplates(
    organizationId: string,
  ): Promise<{ data: CampaignTemplate[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaign_templates")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error getting campaign templates:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get a campaign by ID
   */
  async getCampaignTemplate(
    id: string,
  ): Promise<{ data: CampaignTemplate | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaign_templates")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error getting campaign template:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update a campaign
   */
  async updateCampaignTemplate(
    id: string,
    updates: CampaignTemplateUpdate,
  ): Promise<{ data: CampaignTemplate | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaign_templates")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error updating campaign template:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update campaign status
   */
  async updateCampaignTemplateStatus(
    id: string,
    status: CampaignTemplateStatus,
  ): Promise<{ data: CampaignTemplate | null; error: Error | null }> {
    return this.updateCampaignTemplate(id, { status });
  },

  /**
   * Delete a campaign
   */
  async deleteCampaignTemplate(
    id: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      // First delete all campaign leads
      const { error: deleteLeadsError } = await supabase
        .from("campaign_template_leads")
        .delete()
        .eq("campaign_template_id", id);

      if (deleteLeadsError) {
        throw deleteLeadsError;
      }

      // Then delete the campaign
      const { error } = await supabase
        .from("campaign_templates")
        .delete()
        .eq("id", id);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error deleting campaign template:", error);
      return { success: false, error: error as Error };
    }
  },

  /**
   * Add leads to a campaign
   */
  async addLeadsToCampaignTemplate(
    campaignTemplateId: string,
    leadIds: string[],
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      // Create campaign_leads entries
      const campaignTemplateLeads: CampaignTemplateLeadInsert[] = leadIds.map(
        (leadId) => ({
          campaign_template_id: campaignTemplateId,
          lead_id: leadId,
        }),
      );

      // Just use regular insert - the unique constraint will handle duplicates
      const { error } = await supabase
        .from("campaign_template_leads")
        .insert(campaignTemplateLeads);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error adding leads to campaign template:", error);
      return { success: false, error: error as Error };
    }
  },

  /**
   * Remove a lead from a campaign
   */
  async removeLeadFromCampaignTemplate(
    campaignTemplateId: string,
    leadId: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from("campaign_template_leads")
        .delete()
        .eq("campaign_template_id", campaignTemplateId)
        .eq("lead_id", leadId);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error removing lead from campaign template:", error);
      return { success: false, error: error as Error };
    }
  },

  /**
   * Get all leads for a campaign
   */
  async getCampaignTemplateLeads(
    campaignTemplateId: string,
  ): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaign_template_leads")
        .select(
          `
          id,
          added_at,
          leads:lead_id (*)
        `,
        )
        .eq("campaign_template_id", campaignTemplateId);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error getting campaign template leads:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get campaign statistics
   */
  async getCampaignTemplateStats(
    campaignTemplateId: string,
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      // Get the total number of leads in the campaign
      const { count, error: countError } = await supabase
        .from("campaign_template_leads")
        .select("id", { count: "exact" })
        .eq("campaign_template_id", campaignTemplateId);

      if (countError) {
        throw countError;
      }

      return {
        data: {
          totalLeads: count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error("Error getting campaign template stats:", error);
      return { data: null, error: error as Error };
    }
  },
};